import type { ServiceLine } from 'api/types/service.types';
import { CareemTruckIcon } from 'assets/icons';
import OptionItem from 'components/molecules/OptionItem';
import { useConfig } from 'context/app-config';
import {
  selectHasSelectedDriverPreferences,
  selectPreferenceType,
  selectSelectedServices,
  selectSelectedServicesCount,
} from 'pages/newOrder/newOrder.selectors';
import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from 'redux-stores/reducers/rootReducer';
import { type DefaultTheme, styled } from 'styled-components';

import { DriverInstructionsBottomSheet } from './DriverInstructionsBottomSheet';

interface IconProps {
  theme: DefaultTheme;
  isActive?: boolean;
  isStroke?: boolean;
  isRemoveStroke?: boolean;
  disabled?: boolean;
}

const withIconStyles = (IconComponent: React.FC<React.SVGProps<any>>) => styled(IconComponent)<IconProps>`
  ${({ disabled, theme, isActive }) =>
    !disabled &&
    `
    > path,
    > g > path:nth-child(1) {
      fill: ${isActive ? theme.icon?.activeColor : theme.icon?.color};
    }

    > g > path:nth-child(2),
    > g > path:nth-child(3) {
      fill: ${isActive ? theme.icon?.activeColorBg : theme.icon?.color};
      opacity: 0.6;
    }
  `}

  ${({ disabled, theme }) =>
    disabled &&
    `
    > path,
    > g > path:nth-child(1) {
      fill: ${disabled ? theme.colors.cadetBlue : theme.colors.grey100};
    }

    > g > path:nth-child(2),
    > g > path:nth-child(3) {
      fill: ${disabled ? theme.colors.cadetBlue : theme.colors.grey100};
    }
  `}
`;

const StyledStartIcon = withIconStyles(CareemTruckIcon);

const StyledOptionItem = styled(OptionItem)`
  padding-top: 11px;
  padding-bottom: 11px;
`;

const StyledTag = styled.p`
  color: ${({ theme }) => theme.colors.grey100};
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 20px; /* 142.857% */
  margin-bottom: 3px;
`;

interface DriverInstructionsOptionsProps {
  isDisabled?: boolean;
}

interface DriverInstructionInfoProps {
  serviceLine?: ServiceLine;
  displayCollection?: boolean;
  displayDelivery?: boolean;
  show?: boolean;
}

const DriverInstructionInfo = ({ serviceLine, displayCollection, displayDelivery, show }: DriverInstructionInfoProps) => {
  const { t } = useTranslation();
  const serviceLinesSelectedCount = useSelector(selectSelectedServicesCount);
  const driverPickupInstructionsPreference = useSelector((state: RootState) => selectPreferenceType(state, 'driverPickupInstructions', serviceLine));

  const driverDropoffInstructionsPreference = useSelector((state: RootState) =>
    selectPreferenceType(state, 'driverDropoffInstructions', serviceLine)
  );

  if (!show) return null;

  return (
    <>
      {displayCollection && (
        <StyledTag>{`${displayCollection ? `${t('common.pickUp')}: ${driverPickupInstructionsPreference || 'No preference'}\n` : ''}`}</StyledTag>
      )}
      {displayDelivery && (
        <StyledTag>
          {`${displayDelivery ? `${t(`common.${serviceLinesSelectedCount > 1 ? serviceLine : '' ?? ''}delivery`)}: ${driverDropoffInstructionsPreference || 'No preference'}` : ''}`}
        </StyledTag>
      )}
    </>
  );
};

const DriverInstructionsOptions = ({ isDisabled }: DriverInstructionsOptionsProps) => {
  const { t } = useTranslation();
  const config = useConfig();
  const [showDriverInstructionSheet, setShowDriverInstructionSheet] = React.useState(false);
  const hasSupportForMultipleDropoffs = config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs;
  const selectedServiceLines = useSelector(selectSelectedServices);
  const hasSelectedDriverPreferences = useSelector(selectHasSelectedDriverPreferences);
  const toggleDriverInstructionPageSheet = useCallback(() => setShowDriverInstructionSheet((prev) => !prev), []);

  const handleClosePInstructionSheet = useCallback(() => {
    setShowDriverInstructionSheet(false);
  }, [setShowDriverInstructionSheet]);

  const driverInstructionsInfo = useMemo(() => {
    const tags = [];
    if (hasSupportForMultipleDropoffs) {
      for (let i = 0; i < selectedServiceLines.length; i += 1) {
        const serviceLine = selectedServiceLines[i];
        tags.push(
          <DriverInstructionInfo
            key={`${serviceLine + i}`}
            show={!isDisabled}
            displayCollection={i === 0}
            displayDelivery
            serviceLine={serviceLine}
          />
        );
      }
    } else {
      tags.push(<DriverInstructionInfo key="DriverInstructionInfo-" show={!isDisabled} />);
    }

    return tags;
  }, [hasSupportForMultipleDropoffs, isDisabled, selectedServiceLines]);

  return (
    <>
      <StyledOptionItem
        title={t('newOrderPage.instructionOptions.driverInstructions')}
        startIcon={<StyledStartIcon isActive={!hasSelectedDriverPreferences} disabled={isDisabled} />}
        actionType="edit"
        disabled={isDisabled}
        hideItemSeparator
        hasRightArrowOnly={!isDisabled}
        onClick={toggleDriverInstructionPageSheet}
        value={hasSelectedDriverPreferences ? driverInstructionsInfo : ''}
        isEditable
        hasNoActiveState
      />
      <DriverInstructionsBottomSheet show={showDriverInstructionSheet} onClose={handleClosePInstructionSheet} />
    </>
  );
};

export default DriverInstructionsOptions;
